import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { FormattedMessage as T, useIntl } from 'react-intl';
import { Cloud$Market } from '../../../types/cloudEnums';
import sharedMessages from '../../Translations/messages';
import { ErrorMessage, Field, Input, Label } from '../FormElements';
import m from '../messages';
import { COUNTRY_CALLING_CODES } from './countryCallingCodes';
import { ERROR } from './errors';
import MarketDropdownSelect from './MarketDropdownSelect';
import validatePhoneNumber from './validatePhoneNumberAPI';

export type ValidatedPhoneNumberImperativeActions = {
  setIsRequired: () => void;
  validatePhoneNumber: () => Promise<string | null>;
};

type Props = {
  defaultMarket: Cloud$Market;
} & Omit<ComponentProps<typeof Input>, 'type'>;

type ValidationEnum = 'UNKNOWN' | 'VALID' | 'INVALID';

/**
 * ValidatedPhoneNumberInput
 *
 * Component that let you select a market and validate a phone number. Uses
 * Cloud's /web/user_validation/validate_phone_number/ API under the hood to do
 * validation
 */

const ValidatedPhoneNumberInput = (
  { defaultMarket, className, ...rest }: Props,
  ref,
) => {
  const [selectedMarket, setSelectedMarket] = useState(defaultMarket);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validationState, setValidationState] =
    useState<ValidationEnum>('UNKNOWN');
  const [errorMessage, setErrorMessage] = useState('');

  const intl = useIntl();

  const marketList = Object.keys(COUNTRY_CALLING_CODES) as Array<
    keyof typeof COUNTRY_CALLING_CODES
  >;

  const validateNumber = async () => {
    if (validationState === 'VALID') {
      // The phone number is valid and it hasn't changed since last time. Just
      // return the previous result
      return phoneNumber;
    }
    if (!phoneNumber || validationState === 'INVALID') {
      // To avoid spamming our servers, dont' try to validate it again.
      setErrorMessage(intl.formatMessage(m.phoneNumberRequired));
      return null;
    }

    try {
      const { formatted, countryCode } = await validatePhoneNumber(
        phoneNumber,
        selectedMarket,
      );

      setPhoneNumber(formatted);
      setValidationState('VALID');

      const marketDerivedFromNumber = marketList.find(
        (market) => Number(countryCode) === COUNTRY_CALLING_CODES[market],
      );

      if (marketDerivedFromNumber) {
        setSelectedMarket(marketDerivedFromNumber);
      }

      setErrorMessage('');
      return formatted;
    } catch (error) {
      setValidationState('INVALID');

      if (error.message === ERROR.INVALID_NUMBER) {
        setErrorMessage(intl.formatMessage(m.invalidPhoneNumber));
        return null;
      }
      setErrorMessage(intl.formatMessage(m.unableToValidate));
      return null;
    }
  };

  useImperativeHandle(ref, () => ({
    setIsRequired: () =>
      setErrorMessage(intl.formatMessage(m.phoneNumberRequired)),
    validatePhoneNumber: validateNumber,
  }));

  const handleMarketChange = (changedItem: Cloud$Market) => {
    setValidationState('UNKNOWN');
    setSelectedMarket(changedItem);
  };

  const handlePhoneNumberChange = (ev) => {
    const next = ev.currentTarget.value;
    setErrorMessage('');
    setValidationState('UNKNOWN');
    setPhoneNumber(next);
  };

  return (
    <Field className={className}>
      <Label>
        <T {...sharedMessages.phoneNumber} />
      </Label>

      <div className="relative flex flex-row">
        <div className="mr-3 self-start">
          <MarketDropdownSelect
            onChange={handleMarketChange}
            items={marketList}
            selectedItem={selectedMarket}
            displayItem={(market) => `+${COUNTRY_CALLING_CODES[market]}`}
          />
        </div>
        <div className="flex-1">
          <Input
            placeholder={intl.formatMessage(m.phoneNumberPlaceholder)}
            {...rest}
            className="mb-1"
            type="tel"
            onBlur={validateNumber}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            data-testid="contact-phone-number"
          />
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </div>
      </div>
    </Field>
  );
};

export default forwardRef(ValidatedPhoneNumberInput);
